import { Injectable, inject } from "@angular/core";
import { Observable, combineLatest, map } from "rxjs";
import { Store } from "@ngrx/store";
import { FromRouter, FromUser, RootState, UserActions } from "src/app/store";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import { AppRoutes, QueryParams } from "src/app/constants";
import { jwtDecode } from "jwt-decode";
import { DecodedToken } from "src/app/interfaces";
import { isLoginCallbackInFlight } from "src/app/utils/organization-access";

@Injectable({ providedIn: "root" })
export class AuthGuard {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);

  private readonly programId$: Observable<string | undefined> = this.store.select(FromRouter.selectProgramId);
  private readonly accessToken$: Observable<string | undefined> = this.store.select(FromUser.selectAccessToken);

  public canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return combineLatest([this.programId$, this.accessToken$]).pipe(
      map(([programId, accessToken]) => {
        try {
          const decoded = jwtDecode<DecodedToken>(accessToken!);

          // While a login callback is still being handled the URL keeps its single-use code. Starting
          // another login here would re-exchange that code, which Keycloak rejects and which ends with
          // the user on its logout page instead of in the app.
          if (decoded.organization !== programId && !isLoginCallbackInFlight(window.location.search)) {
            this.store.dispatch(UserActions.loadKeycloakLogin());
          }
        } catch {}

        if (accessToken) return true;

        // Carry the attempted URL on the login route so it can be restored after the Keycloak
        // round-trip. Passing it explicitly (rather than persisting it here) lets the login flow
        // tell a guard-initiated sign-in apart from a direct /login visit, so a stale target from
        // an abandoned earlier attempt can't hijack the destination.
        return this.router.createUrlTree([AppRoutes.root, programId, AppRoutes.login], {
          queryParams: { [QueryParams.redirect]: state.url },
        });
      }),
    );
  }
}
